import { useNotify } from '@globaleris/ui.library'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity } from '../../api'
import { VirtualBatteryBalanceModel } from '../../api/Argentia/endpoints/floatingConcepts/model/floatingConcepts'
import CommodityFilter from '../../components/shared/commodityFilter/commodityFilter'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import { enumToMapValues, enumValueToKey, MapToArray } from '../../helpers'
import { hasResource } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks'
import { Resources } from '../../model/RBAC/constants'
import {
  getActiveCommodities,
  getActiveCommodity,
  getActiveCommodityFilter,
  storeActiveTabForCurrentPage,
  storeCommodityFilterForCurrentPage
} from '../../redux'
import { FloatingConceptsBalanceActionsTab } from './components/floatingConceptsBalanceActionsTab'
import { FloatingConceptsDefaultTab } from './components/floatingConceptsDefaultTab'
import { FloatingConceptsVirtualBatteryTab } from './components/floatingConceptsVirtualBatteryTab'
import { mapVBListToTableModel } from './floatingConcepts.mapper'
import { EnumFloatingConceptsTabs } from './floatingConcepts.model'
import { fetchVirtualBatteryBalance } from './floatingConcepts.service'
import { Box } from '@material-ui/core'

export interface FloatingConceptsPageProps {}

export const FloatingConceptsPage: React.FC<FloatingConceptsPageProps> = () => {
  const [t] = useTranslation([
    'globalerisLibrary',
    'enums',
    'translation',
    'errorDetailList',
    'floatingConcepts'
  ])
  const { info, error } = useNotify()
  const { activeTab } = usePageState()

  // Commodity filter initialization
  const commodities = getActiveCommodities()
  const activeCommodityFilter = getActiveCommodityFilter()

  const initialCommodityFilter: EnumCommodity =
    getActiveCommodity() ??
    (activeCommodityFilter
      ? activeCommodityFilter
      : commodities !== undefined && commodities?.length > 0
        ? enumValueToKey(EnumCommodity, commodities[0])
        : EnumCommodity.GasNatural)

  const [virtualBatteryList, setVirtualBatteryList] = useState<VirtualBatteryBalanceModel[]>([])
  const [currentTab, setCurrentTab] = useState<number>(activeTab ?? 0)
  const [commodity, setCommodity] = useState<EnumCommodity>(initialCommodityFilter)
  const tabsArray = MapToArray(enumToMapValues(EnumFloatingConceptsTabs))

  // ------------------- Commodity Handler -------- //
  const onSelectCommodityHandler = useCallback((selectedCommodity: EnumCommodity): void => {
    setCommodity(selectedCommodity)
    storeCommodityFilterForCurrentPage(selectedCommodity)
  }, [])

  const renderCommodities = () => {
    if (filteredTabsArray[currentTab]?.id === EnumFloatingConceptsTabs.balanceActions) {
      return null
    }
    if (commodities !== undefined && commodities.length > 1) {
      return (
        <CommodityFilter
          commodities={[
            { commodity: enumValueToKey(EnumCommodity, commodities[0]) },
            { commodity: enumValueToKey(EnumCommodity, commodities[1]) }
          ]}
          commodity={commodity}
          enabled={true}
          inactive={false}
          onSelectCommodity={onSelectCommodityHandler}
        />
      )
    }
    return
  }

  // Filters Region
  // End Filters Region

  const getVirtualBatteryList = async () => {
    await fetchVirtualBatteryBalance().then(response => {
      if (response.success && response.data) {
        setVirtualBatteryList(response.data)
      } else {
        error('floatingConcepts:notifications.getVirtualBatteryList.error')
      }
    })
  }

  // #region Tabs
  const handleTabPermission = (tabValue: number): boolean => {
    switch (tabValue) {
      case EnumFloatingConceptsTabs.virtualBattery:
        return hasResource(Resources.VirtualBattery) && commodity === EnumCommodity.Electricidad
      case EnumFloatingConceptsTabs.balanceActions:
        return hasResource(Resources.BalanceActions)
      default:
        return hasResource(Resources.FloatingConcepts)
    }
  }

  const tabsDefinition: Map<number, Tab> = new Map(
    tabsArray.map((tab, tabIndex) => [
      tabIndex,
      {
        id: tab.value,
        label: t(`floatingConcepts:tabs.${tab.name}`),
        hasPermission: handleTabPermission(tab.value)
      }
    ])
  )

  // filter tabs with permissions to avoid getting hidden tabs values onTabChange
  const filteredTabsArray = Array.from(tabsDefinition.values()).filter(tab => tab.hasPermission)

  const onTabChangeHandler = (tabIdx: number) => {
    if (currentTab !== tabIdx) {
      setCurrentTab(tabIdx)
      storeActiveTabForCurrentPage(tabIdx)
    }
  }

  const renderTabContent = (activeTab: number) => {
    switch (filteredTabsArray[activeTab]?.id) {
      case EnumFloatingConceptsTabs.distribucion:
        return (
          <FloatingConceptsDefaultTab
            activeTab={EnumFloatingConceptsTabs.distribucion}
            commodity={commodity}
            onSelectCommodity={onSelectCommodityHandler}
          />
        )
      case EnumFloatingConceptsTabs.refacturacion:
        return (
          <FloatingConceptsDefaultTab
            activeTab={EnumFloatingConceptsTabs.refacturacion}
            commodity={commodity}
            onSelectCommodity={onSelectCommodityHandler}
          />
        )
      case EnumFloatingConceptsTabs.penalizacion:
        return (
          <FloatingConceptsDefaultTab
            activeTab={EnumFloatingConceptsTabs.penalizacion}
            commodity={commodity}
            onSelectCommodity={onSelectCommodityHandler}
          />
        )
      case EnumFloatingConceptsTabs.manual:
        return (
          <FloatingConceptsDefaultTab
            activeTab={EnumFloatingConceptsTabs.manual}
            commodity={commodity}
            onSelectCommodity={onSelectCommodityHandler}
          />
        )
      case EnumFloatingConceptsTabs.virtualBattery:
        return (
          <FloatingConceptsVirtualBatteryTab
            tableData={mapVBListToTableModel(virtualBatteryList) ?? []}
            getVirtualBatteryList={getVirtualBatteryList}
          />
        )
      case EnumFloatingConceptsTabs.balanceActions:
        return <FloatingConceptsBalanceActionsTab />
      default:
        return (
          <FloatingConceptsDefaultTab
            activeTab={EnumFloatingConceptsTabs.distribucion}
            commodity={commodity}
            onSelectCommodity={onSelectCommodityHandler}
          />
        )
    }
  }

  // #endregion Tabs

  useEffect(() => {
    let tabToSet = activeTab ?? 0

    if (filteredTabsArray.length > 0 && tabToSet >= filteredTabsArray.length) {
      tabToSet = 0
    }

    if (currentTab !== tabToSet) {
      setCurrentTab(tabToSet)
      storeActiveTabForCurrentPage(tabToSet)
    }
  }, [activeTab, commodity])

  return (
    <Page>
      <PageHeader title={t('floatingConcepts:title')}>
        <Box display="flex" justifyContent="flex-end">
          {renderCommodities()}
        </Box>
      </PageHeader>
      <TabsWithPermissions
        tabs={tabsDefinition}
        tabDefault={currentTab}
        onTabChange={onTabChangeHandler}
      ></TabsWithPermissions>
      {renderTabContent(activeTab ? activeTab : currentTab)}
    </Page>
  )
}

export default FloatingConceptsPage
